import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

// Use localStorage for persistent login across sessions
const TOKEN_KEY = 'mf_token';

const getStoredToken = () => localStorage.getItem(TOKEN_KEY);
const setStoredToken = (t) => localStorage.setItem(TOKEN_KEY, t);
const removeStoredToken = () => localStorage.removeItem(TOKEN_KEY);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(getStoredToken);

  const checkAuth = useCallback(async () => {
    if (window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }

    const storedToken = getStoredToken();
    if (!storedToken) {
      setLoading(false);
      return;
    }

    try {
      const response = await axios.get(`${API_URL}/api/auth/me`, {
        headers: { Authorization: `Bearer ${storedToken}` }
      });
      setUser(response.data);
      setToken(storedToken);
      // Auto-refresh token if backend sends a new one
      if (response.data.refreshed_token) {
        setStoredToken(response.data.refreshed_token);
        setToken(response.data.refreshed_token);
      }
    } catch (err) {
      // Only clear token on explicit 401 (unauthorized/expired)
      // Keep the token on network errors, 500s, etc. so user stays logged in
      if (err.response && err.response.status === 401) {
        removeStoredToken();
        setToken(null);
        setUser(null);
      } else {
        // Network error or server error — keep user logged in with stale data
        // Set token so protected routes don't redirect
        setToken(storedToken);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = async (email, password) => {
    const response = await axios.post(`${API_URL}/api/auth/login`, { email, password });
    const { token: newToken, user: userData } = response.data;
    setStoredToken(newToken);
    setToken(newToken);
    setUser(userData);
    return userData;
  };

  const register = async (userData) => {
    const response = await axios.post(`${API_URL}/api/auth/register`, userData);
    const { token: newToken, user: newUser } = response.data;
    setStoredToken(newToken);
    setToken(newToken);
    setUser(newUser);
    return newUser;
  };

  const loginWithGoogle = () => {
    const redirectUrl = window.location.origin + '/auth/callback';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const processGoogleSession = async (sessionId) => {
    const response = await axios.post(
      `${API_URL}/api/auth/google/session`,
      { session_id: sessionId }
    );
    const { token: newToken, user: userData } = response.data;
    setStoredToken(newToken);
    setToken(newToken);
    setUser(userData);
    return userData;
  };

  const logout = async () => {
    try {
      await axios.post(`${API_URL}/api/auth/logout`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
    } catch { /* ignore */ }
    removeStoredToken();
    setToken(null);
    setUser(null);
  };

  const updateUser = (userData) => {
    setUser(prev => ({ ...prev, ...userData }));
  };

  return (
    <AuthContext.Provider value={{
      user, token, loading,
      login, register, loginWithGoogle, processGoogleSession,
      logout, updateUser,
      isAuthenticated: !!user
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
